import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tooltip as TooltipUI, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { 
  ArrowLeft, Loader2, Building2, Users, Truck, FileText, 
  Receipt, Settings, TrendingUp, TrendingDown, DollarSign, Calendar,
  Pause, Play
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, ComposedChart, Bar, CartesianGrid, Tooltip as RechartsTooltip } from 'recharts';
import AdminClientTeamCard from '../components/AdminClientTeamCard';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminClientViewPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [dailyModalOpen, setDailyModalOpen] = useState(false);
  const [dailyData, setDailyData] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [togglingStatus, setTogglingStatus] = useState(false);

  const handleToggleStatus = async () => {
    if (!data?.client) return;
    const currentlyActive = data.client.is_active !== false; // default true if missing
    const next = !currentlyActive;
    const verb = next ? 're-enable' : 'suspend';
    if (!window.confirm(
      next
        ? `Re-enable ${data.client.email}?\n\nThey'll regain access immediately. All their data is intact.`
        : `Suspend ${data.client.email}?\n\nThey will be blocked from logging in and existing sessions will be invalidated. All their data is preserved — you can re-enable any time.`
    )) return;
    setTogglingStatus(true);
    try {
      await axios.put(`${API}/admin/clients/${clientId}/status`, null, {
        params: { is_active: next },
      });
      toast.success(next ? `${data.client.email} re-enabled` : `${data.client.email} suspended`);
      await fetchClientDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${verb} account`);
    } finally {
      setTogglingStatus(false);
    }
  };

  useEffect(() => {
    fetchClientDashboard();
  }, [clientId]);

  const fetchClientDashboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/clients/${clientId}/dashboard`);
      setData(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load client data');
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyData = async (year, month, monthName) => {
    setDailyLoading(true);
    setSelectedMonth({ year, month, name: monthName });
    setDailyModalOpen(true);
    try {
      const response = await axios.get(`${API}/admin/clients/${clientId}/daily/${year}/${month}`);
      setDailyData(response.data);
    } catch (error) {
      toast.error('Failed to load daily cash flow data');
    } finally {
      setDailyLoading(false);
    }
  };

  const handleMonthDoubleClick = (monthData) => {
    fetchDailyData(monthData.year, monthData.month, monthData.month_label);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatCompactCurrency = (value) => {
    if (Math.abs(value) >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (Math.abs(value) >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return formatCurrency(value);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!data || !data.business) {
    return (
      <Layout>
        <div className="text-center py-20">
          <p className="text-slate-500">Client has no business data yet</p>
          <Button onClick={() => navigate('/admin')} className="mt-4">
            Back to Admin
          </Button>
        </div>
      </Layout>
    );
  }

  const chartData = data.cash_flow.map(m => ({
    name: m.month_label.split(' ')[0],
    closing: m.closing_cash
  }));

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin
          </Button>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {data.client.name}
              </h1>
              {data.client.is_active === false && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-700 border border-red-200"
                  data-testid="client-suspended-badge"
                  title="This account is suspended — the client cannot log in."
                >
                  <Pause className="w-3 h-3" />
                  Suspended
                </span>
              )}
            </div>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              {data.client.email} &bull; Read-only view
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={handleToggleStatus}
              disabled={togglingStatus}
              className={`gap-1.5 ${data.client.is_active === false ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 border-emerald-200' : 'text-amber-600 hover:text-amber-700 hover:bg-amber-50 border-amber-200'}`}
              title={data.client.is_active === false
                ? 'Re-enable this client (they can log in immediately)'
                : 'Suspend this client without deleting their data (one-click re-enable later)'}
              data-testid="client-toggle-status-btn"
            >
              {togglingStatus ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : data.client.is_active === false ? (
                <Play className="w-3.5 h-3.5" />
              ) : (
                <Pause className="w-3.5 h-3.5" />
              )}
              {data.client.is_active === false ? 'Re-enable' : 'Suspend'}
            </Button>
            <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 px-3 py-2 rounded-lg">
              <Building2 className="w-4 h-4 text-slate-500" />
              <span className="font-medium">{data.business?.name || 'No Business'}</span>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-blue-600" />
                <span className="text-xs text-slate-500">Customers</span>
              </div>
              <p className="text-xl font-bold" data-testid="admin-client-customers">{data.summary.total_customers}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1">
                <Truck className="w-4 h-4 text-orange-600" />
                <span className="text-xs text-slate-500">Suppliers</span>
              </div>
              <p className="text-xl font-bold" data-testid="admin-client-suppliers">{data.summary.total_suppliers}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4 text-green-600" />
                <span className="text-xs text-slate-500">Invoices</span>
              </div>
              <p className="text-xl font-bold" data-testid="admin-client-invoices">{data.summary.total_invoices}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1">
                <Receipt className="w-4 h-4 text-red-600" />
                <span className="text-xs text-slate-500">Bills</span>
              </div>
              <p className="text-xl font-bold" data-testid="admin-client-bills">{data.summary.total_bills}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-green-600" />
                <span className="text-xs text-slate-500">Total AR</span>
              </div>
              <p className="text-lg font-bold font-mono">{formatCompactCurrency(data.summary.total_ar)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-1">
                <TrendingDown className="w-4 h-4 text-red-600" />
                <span className="text-xs text-slate-500">Total AP</span>
              </div>
              <p className="text-lg font-bold font-mono">{formatCompactCurrency(data.summary.total_ap)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Settings Overview */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Forecast Settings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-slate-500">Opening Balance Date</p>
                <p className="font-medium">
                  {data.settings.opening_balance_date || 
                    `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][data.settings.start_month - 1]} ${data.settings.start_year}`}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Opening Cash</p>
                <p className="font-medium font-mono">{formatCurrency(data.settings.opening_cash)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Account Created</p>
                <p className="font-medium">{new Date(data.client.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Current Closing Cash</p>
                <p className={`font-medium font-mono ${data.cash_flow[data.cash_flow.length - 1]?.closing_cash >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(data.cash_flow[data.cash_flow.length - 1]?.closing_cash || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Team Management */}
        {data?.client && (
          <AdminClientTeamCard
            clientId={clientId}
            clientEmail={data.client.email}
            businessName={data.business?.name}
          />
        )}

        {/* Cash Flow Chart */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Closing Cash Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis 
                    tickFormatter={(v) => formatCompactCurrency(v)} 
                    tick={{ fontSize: 12 }}
                    width={70}
                  />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Closing Cash']}
                    labelStyle={{ fontWeight: 'bold' }}
                  />
                  <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="3 3" />
                  <Line 
                    type="monotone" 
                    dataKey="closing" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Cash Flow Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              13-Month Cash Flow Forecast
              <span className="text-xs font-normal text-slate-500 ml-2">(Double-click a month for daily breakdown)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-white dark:bg-slate-900 z-10">Month</TableHead>
                    <TableHead className="text-right">Opening</TableHead>
                    <TableHead className="text-right text-green-700">AR Receipts</TableHead>
                    <TableHead className="text-right text-red-700">AP Payments</TableHead>
                    <TableHead className="text-right text-green-700">Other In</TableHead>
                    <TableHead className="text-right text-red-700">Other Out</TableHead>
                    <TableHead className="text-right font-semibold">Net Movement</TableHead>
                    <TableHead className="text-right font-semibold">Closing</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.cash_flow.map((month, idx) => (
                    <TableRow 
                      key={idx} 
                      className={`cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/10 ${month.closing_cash < 0 ? 'bg-red-50 dark:bg-red-900/10' : ''}`}
                      onDoubleClick={() => handleMonthDoubleClick(month)}
                      data-testid={`admin-month-row-${idx}`}
                    >
                      <TableCell className="sticky left-0 bg-white dark:bg-slate-900 font-medium z-10">
                        {month.month_label}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCurrency(month.opening_cash)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-green-700">
                        {month.ar_receipts > 0 ? formatCurrency(month.ar_receipts) : '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-red-700">
                        {month.ap_payments > 0 ? `(${formatCurrency(month.ap_payments)})` : '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-green-700">
                        {month.other_in > 0 ? formatCurrency(month.other_in) : '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-red-700">
                        {month.other_out > 0 ? `(${formatCurrency(month.other_out)})` : '-'}
                      </TableCell>
                      <TableCell className={`text-right font-mono text-sm font-semibold ${month.net_movement >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {month.net_movement >= 0 ? formatCurrency(month.net_movement) : `(${formatCurrency(Math.abs(month.net_movement))})`}
                      </TableCell>
                      <TableCell className={`text-right font-mono text-sm font-bold ${month.closing_cash >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {formatCurrency(month.closing_cash)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Daily Drill-Down Modal */}
        <Dialog open={dailyModalOpen} onOpenChange={setDailyModalOpen}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Calendar className="w-5 h-5 text-primary" />
                Daily Cash Flow - {selectedMonth?.name}
                <span className="text-xs font-normal text-slate-500 ml-2">(Read-only)</span>
              </DialogTitle>
            </DialogHeader>
            
            {dailyLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : dailyData ? (
              <TooltipProvider>
              <div className="flex-1 overflow-auto space-y-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
                    <p className="text-xs text-slate-500 uppercase">Opening</p>
                    <p className="text-lg font-bold font-mono">{formatCurrency(dailyData.opening_cash)}</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                    <p className="text-xs text-green-600 uppercase">Total AR In</p>
                    <p className="text-lg font-bold font-mono text-green-600">{formatCurrency(dailyData.totals.ar_in)}</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
                    <p className="text-xs text-red-600 uppercase">Total AP Out</p>
                    <p className="text-lg font-bold font-mono text-red-600">{formatCurrency(dailyData.totals.ap_out)}</p>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                    <p className="text-xs text-blue-600 uppercase">Net Movement</p>
                    <p className={`text-lg font-bold font-mono ${dailyData.totals.net_movement >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(dailyData.totals.net_movement)}
                    </p>
                  </div>
                  <div className="bg-primary/10 rounded-lg p-3">
                    <p className="text-xs text-primary uppercase">Month End</p>
                    <p className={`text-lg font-bold font-mono ${dailyData.daily_data[dailyData.daily_data.length - 1]?.closing_cash >= 0 ? 'text-slate-900 dark:text-white' : 'text-red-600'}`}>
                      {formatCurrency(dailyData.daily_data[dailyData.daily_data.length - 1]?.closing_cash || 0)}
                    </p>
                  </div>
                </div>

                {/* Daily Chart */}
                <div className="h-48 bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={dailyData.daily_data} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="day" stroke="#64748b" fontSize={10} />
                      <YAxis 
                        stroke="#64748b" 
                        fontSize={10}
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <RechartsTooltip 
                        formatter={(value) => [formatCurrency(value)]}
                        contentStyle={{ fontSize: '12px' }}
                      />
                      <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
                      <Bar dataKey="ar_in" fill="#22c55e" name="AR In" stackId="positive" />
                      <Bar dataKey="other_in" fill="#3b82f6" name="Other In" stackId="positive" />
                      <Bar dataKey="ap_out" fill="#ef4444" name="AP Out" stackId="negative" />
                      <Bar dataKey="other_out" fill="#f97316" name="Other Out" stackId="negative" />
                      <Line type="monotone" dataKey="closing_cash" stroke="#1e3a8a" strokeWidth={2} dot={false} name="Closing" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>

                {/* Daily Data Table */}
                <div className="border rounded-lg overflow-auto max-h-[300px]">
                  <Table>
                    <TableHeader className="sticky top-0 bg-white dark:bg-slate-900 z-10">
                      <TableRow className="bg-slate-50 dark:bg-slate-800">
                        <TableHead className="text-xs font-semibold">Day</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Opening</TableHead>
                        <TableHead className="text-xs font-semibold text-right">AR In</TableHead>
                        <TableHead className="text-xs font-semibold text-right">AP Out</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Other In</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Other Out</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Net</TableHead>
                        <TableHead className="text-xs font-semibold text-right">Closing</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dailyData.daily_data.map((day) => {
                        const hasActivity = day.ar_in > 0 || day.ap_out > 0 || day.other_in > 0 || day.other_out > 0;
                        const hasTransactions = day.transactions && day.transactions.length > 0;
                        
                        const rowContent = (
                          <TableRow 
                            key={day.day}
                            className={`${day.closing_cash < 0 ? 'bg-red-50 dark:bg-red-900/10' : ''} ${hasActivity ? '' : 'text-slate-400'} ${hasTransactions ? 'cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/10' : ''}`}
                          >
                            <TableCell className="font-medium text-sm">
                              <div className="flex items-center gap-1">
                                <span>{day.day}</span>
                                {hasTransactions && (
                                  <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                                    {day.transactions.length}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-mono text-xs">{formatCurrency(day.opening_cash)}</TableCell>
                            <TableCell className="text-right font-mono text-xs text-green-600">{day.ar_in > 0 ? formatCurrency(day.ar_in) : '-'}</TableCell>
                            <TableCell className="text-right font-mono text-xs text-red-600">{day.ap_out > 0 ? formatCurrency(day.ap_out) : '-'}</TableCell>
                            <TableCell className="text-right font-mono text-xs text-green-600">{day.other_in > 0 ? formatCurrency(day.other_in) : '-'}</TableCell>
                            <TableCell className="text-right font-mono text-xs text-red-600">{day.other_out > 0 ? formatCurrency(day.other_out) : '-'}</TableCell>
                            <TableCell className={`text-right font-mono text-xs font-medium ${day.net_movement >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {day.net_movement !== 0 ? formatCurrency(day.net_movement) : '-'}
                            </TableCell>
                            <TableCell className={`text-right font-mono text-xs font-bold ${day.closing_cash >= 0 ? '' : 'text-red-600'}`}>
                              {formatCurrency(day.closing_cash)}
                            </TableCell>
                          </TableRow>
                        );
                        
                        if (hasTransactions) {
                          return (
                            <TooltipUI key={day.day} delayDuration={200}>
                              <TooltipTrigger asChild>
                                {rowContent}
                              </TooltipTrigger>
                              <TooltipContent 
                                side="right" 
                                className="bg-white dark:bg-slate-800 border shadow-lg p-0 max-w-md"
                                sideOffset={10}
                              >
                                <div className="p-3">
                                  <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2 border-b pb-1">
                                    Day {day.day} - {day.transactions.length} Transaction{day.transactions.length > 1 ? 's' : ''}
                                  </p>
                                  <div className="space-y-1.5 max-h-48 overflow-auto">
                                    {day.transactions.map((t, idx) => (
                                      <div key={idx} className="flex items-center justify-between gap-4 text-xs">
                                        <div className="flex items-center gap-2">
                                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                            t.type === 'AR' ? 'bg-green-100 text-green-700' :
                                            t.type === 'AP' ? 'bg-red-100 text-red-700' :
                                            t.type === 'Other In' ? 'bg-blue-100 text-blue-700' :
                                            'bg-orange-100 text-orange-700'
                                          }`}>{t.type}</span>
                                          <span className="text-slate-600 dark:text-slate-400 truncate max-w-[200px]">{t.counterparty}</span>
                                        </div>
                                        <span className={`font-mono font-medium ${t.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                          {formatCurrency(t.amount)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </TooltipContent>
                            </TooltipUI>
                          );
                        }
                        
                        return <React.Fragment key={day.day}>{rowContent}</React.Fragment>;
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
              </TooltipProvider>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
